// src/components/cards/NewsCard.tsx
import React from 'react';
import { NewsData, NewsSource } from '../../types';
import DataStatusIndicator, { DataStatus } from '../DataStatusIndicator';

interface NewsCardProps {
  data: NewsData | null;
  loading: boolean;
  error: string | null;
  lastUpdated: number;
  formatTimestamp: (timestamp: number) => string;
  dataStatus?: DataStatus;
}

const NewsCard: React.FC<NewsCardProps> = ({
  data,
  loading,
  error,
  lastUpdated,
  formatTimestamp,
  dataStatus = 'live'
}) => {
  // Get color based on mention count
  const getMentionColor = (count: number): string => {
    return count > 0 ? 'var(--color-bull)' : 'var(--color-neutral)';
  };

  // List of sources we search for
  const searchedSources: NewsSource[] = ['CNN', 'NBC', 'CNBC', 'FOX', 'SNL', 'Bloomberg', 'Reuters', 'WSJ'];

  // Get SPX6900 mentions count for a specific source
  const getSourceMentionCount = (source: NewsSource): number => {
    if (!data) return 0;
    return data.articles.filter(article => 
      article.source === source && article.mentions.includes('SPX6900')
    ).length;
  };

  return (
    <div className="bg-dashboard-card-light dark:bg-dashboard-card-dark rounded-card shadow-card p-5 dashboard-card relative">
      <DataStatusIndicator status={dataStatus} lastUpdated={lastUpdated} />
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-semibold">SPX6900 News Mentions</h3>
        {loading && (
          <div className="animate-pulse px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-xs">
            Updating...
          </div>
        )}
      </div>

      {error ? (
        <div className="text-red-500 py-8 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>{error}</p>
        </div>
      ) : data ? (
        <>
          {/* Total SPX6900 Mentions */}
          <div className="mb-4">
            <div className="text-3xl font-bold mb-2" style={{ color: getMentionColor(data.mentionsCount['SPX6900'] || 0) }}>
              {data.mentionsCount['SPX6900'] || 0}
            </div>
            <div className="text-sm text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
              total mentions across news sources
            </div>
          </div>

          {/* Status Indicator */}
          <div className="mb-4">
            <div className={`px-3 py-2 rounded-lg text-center font-medium ${
              (data.mentionsCount['SPX6900'] || 0) > 0 
                ? 'bg-bull/20 text-bull' 
                : 'bg-gray-100 dark:bg-gray-800 text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark'
            }`}>
              {(data.mentionsCount['SPX6900'] || 0) > 0 ? 'DETECTED IN NEWS' : 'NO MENTIONS FOUND'}
            </div>
          </div>

          {/* Sources List with Mention Counts */}
          <div className="mb-4">
            <div className="text-sm font-medium mb-3">Sources Searched</div>
            <div className="space-y-2">
              {searchedSources.map((source) => {
                const mentionCount = getSourceMentionCount(source);
                const hasArticles = data.sourceBreakdown[source] > 0;
                
                return (
                  <div key={source} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex items-center gap-2">
                      {/* API Status Indicator */}
                      {!error ? (
                        <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span className="text-sm font-medium">{source}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm ${mentionCount > 0 ? 'text-bull font-semibold' : 'text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark'}`}>
                        {mentionCount} {mentionCount === 1 ? 'mention' : 'mentions'}
                      </span>
                      {hasArticles && (
                        <span className="text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
                          ({data.sourceBreakdown[source]} articles)
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Last Updated */}
          <div className="mt-2 text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
            Last updated: {formatTimestamp(lastUpdated || data.timestamp)}
          </div>
        </>
      ) : (
        <div className="py-8 text-center text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>No news data available</p>
        </div>
      )}
    </div>
  );
};

export default NewsCard;
